import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { useFetchSingleReference } from 'domains/catalog/Catalog.requests';
import { getLastSearchedVehicleKey } from 'domains/catalog/Catalog.store';
import { PRODUCT_MENU_TABS } from 'domains/catalog/Catalog.types';
import {
  DHReferenceLocal,
  getDHReference,
  getSelectedTab,
  getStocksMapV2,
  LocalTabType,
  setSelectedTab,
} from 'domains/references';
import { useFetchDiscontinuedReferences, useFetchEquivalentReferences } from 'domains/references/References.requests';
import { SparePartsViewType } from 'domains/user';
import { STabs } from 'pages/CatalogPage/DH/Product/Product.styled';
import {
  hasAnyAlternativeReferenceWithStock,
  resolveEquivalentReferenceNumbers,
} from 'pages/CatalogPage/DH/Product/ProductMenu/ProductMenu.utils';
import { ProductMenuTable } from 'pages/CatalogPage/DH/Product/ProductMenu/ProductMenuTable';
import { Flex, LinkRoundButton, MarginBox, Tab, Text } from 'UI';
import { getData } from 'utils';

function SubstituteSection({
  keyRefNumber,
  sparePartsView,
  parentRef,
}: Readonly<{
  keyRefNumber: string;
  sparePartsView: SparePartsViewType;
  parentRef: DHReferenceLocal;
}>) {
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  useFetchSingleReference(keyRefNumber, null, vehicleKey);
  const ref = useSelector((state: RootState) => getDHReference(state, { referenceNumber: keyRefNumber, vehicleKey }));
  const substitutedRefNumber = getData(ref)?.substitutedReference?.referenceNumber;

  if (!substitutedRefNumber) {
    return null;
  }

  return (
    <ProductMenuTable
      referenceNumbers={[substitutedRefNumber]}
      sparePartsView={sparePartsView}
      parentRef={parentRef}
      tabType={'substitute_references'}
    />
  );
}

function ReplacingSection({
  keyRefNumber,
  sparePartsView,
  parentRef,
}: Readonly<{
  keyRefNumber: string;
  sparePartsView: SparePartsViewType;
  parentRef: DHReferenceLocal;
}>) {
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  useFetchSingleReference(keyRefNumber, null, vehicleKey);
  const ref = useSelector((state: RootState) => getDHReference(state, { referenceNumber: keyRefNumber, vehicleKey }));
  const refData = getData(ref);
  const replacingRefs = refData?.replacingReferences ?? [];
  const bestReplacing = refData?.bestReplacingReference;

  if (replacingRefs.length === 0) {
    return null;
  }

  const sorted = [...replacingRefs].sort((a, b) => (a.referenceNumber > b.referenceNumber ? 1 : -1));
  const bestIdx = sorted.findIndex((r) => r.referenceNumber === bestReplacing);

  if (bestIdx > -1) {
    sorted.unshift(sorted.splice(bestIdx, 1)[0]);
  }

  return (
    <ProductMenuTable
      referenceNumbers={sorted.map((r) => r.referenceNumber)}
      sparePartsView={sparePartsView}
      parentRef={parentRef}
      tabType={'substitute_references'}
      bestReplacingReference={bestReplacing}
    />
  );
}

type SectionType = 'direct' | 'substitute-key' | 'replacing-key';

interface TabElements {
  tabName: string;
  itemList: {
    listHeader: React.ReactNode;
    referenceNumbers: string[];
    sectionType: SectionType;
    bestReplacingReference?: string;
  }[];
}

function useTypeSwitch(type: LocalTabType, refLocal: DHReferenceLocal): TabElements {
  const { t } = useTranslation();
  const reference = getData(refLocal);

  const discontinued = useFetchDiscontinuedReferences(reference?.referenceNumber ?? '');
  const isDiscontinued = discontinued?.data ?? false;
  const equivalentReferences = useFetchEquivalentReferences(reference?.referenceNumber ?? '', isDiscontinued);

  const alternativeReferences = reference?.alternativeReferences ?? [];
  const alternativeStocksMap = useSelector((state: RootState) => getStocksMapV2(state, alternativeReferences));
  const hasDatahubReferencesWithStock = hasAnyAlternativeReferenceWithStock(
    alternativeReferences,
    alternativeStocksMap,
  );
  const equivalentReferenceNumbers = resolveEquivalentReferenceNumbers(
    alternativeReferences,
    equivalentReferences?.data ?? [],
    hasDatahubReferencesWithStock,
  );

  switch (type) {
    case 'equivalent_references': {
      return {
        tabName: t('catalog.product.menu.equivalent_references', 'Equivalent references'),
        itemList: [
          {
            listHeader: (
              <Text type={'h3'}>{t('catalog.product.menu.equivalent_references', 'Equivalent references')}</Text>
            ),
            referenceNumbers: equivalentReferenceNumbers,
            sectionType: 'direct',
          },
        ],
      };
    }
    case 'substitute_references':
      return {
        tabName: t('catalog.product.menu.substitutions', 'Substitutions'),
        itemList: [
          {
            listHeader: (
              <Flex>
                <Text type={'h3'}>{t('catalog.product.menu.list.substituted_reference', 'Substituted Reference')}</Text>
                <MarginBox ml={15} />
                <Flex align={'center'}>
                  <LinkRoundButton noPointer />
                </Flex>
              </Flex>
            ),
            referenceNumbers: reference?.initialReferenceNumber
              ? [reference.initialReferenceNumber]
              : reference?.substitutedReference
              ? [reference?.referenceNumber]
              : [],
            sectionType: 'substitute-key',
          },
          {
            listHeader: (
              <Text type={'text_form'}>
                {t('catalog.product.menu.list.replacing_references', 'Replacing references')}
              </Text>
            ),
            referenceNumbers: reference?.initialReferenceNumber
              ? [reference.initialReferenceNumber]
              : reference?.replacingReferences && reference.replacingReferences.length > 0
              ? [reference?.referenceNumber]
              : [],
            sectionType: 'replacing-key',
          },
        ],
      };
    case 'linked_references':
      return {
        tabName: t('catalog.product.menu.linked_products', 'Linked products'),
        itemList: [
          {
            listHeader: <Text type={'h3'}>{t('catalog.product.menu.linked_products', 'Linked products')}</Text>,
            referenceNumbers: [...(reference?.linkedReferences?.map((r) => r.referenceNumber) ?? [])]?.sort((a, b) =>
              a > b ? 1 : -1,
            ),
            sectionType: 'direct',
          },
        ],
      };
    case 'kit':
      return {
        tabName: t('catalog.product.menu.kit', 'Kit'),
        itemList: [
          {
            listHeader: <Text type={'h3'}>{t('catalog.product.menu.kit', 'Kit')}</Text>,
            referenceNumbers: reference?.kitReferences?.map((r) => r.referenceNumber) ?? [],
            sectionType: 'direct',
          },
        ],
      };
    default:
      return {
        tabName: '',
        itemList: [],
      };
  }
}

function renderSection(
  item: TabElements['itemList'][number],
  sparePartsView: SparePartsViewType,
  refLocal: DHReferenceLocal,
  type: LocalTabType,
) {
  switch (item.sectionType) {
    case 'substitute-key':
      return (
        <SubstituteSection
          keyRefNumber={item.referenceNumbers[0]}
          sparePartsView={sparePartsView}
          parentRef={refLocal}
        />
      );
    case 'replacing-key':
      return (
        <ReplacingSection
          keyRefNumber={item.referenceNumbers[0]}
          sparePartsView={sparePartsView}
          parentRef={refLocal}
        />
      );
    default:
      return (
        <ProductMenuTable
          referenceNumbers={item.referenceNumbers}
          sparePartsView={sparePartsView}
          parentRef={refLocal}
          tabType={type}
          bestReplacingReference={item.bestReplacingReference}
        />
      );
  }
}

function LocalTab(refLocal: DHReferenceLocal, sparePartsView: SparePartsViewType, type: LocalTabType) {
  const tabElements = useTypeSwitch(type, refLocal);
  const items = tabElements.itemList.filter((item) => item.referenceNumbers.length > 0);

  if (items.length === 0) {
    return null;
  }

  return (
    <Tab key={type} tab={tabElements.tabName}>
      {items.map((item, index) => (
        <React.Fragment key={`productMenuTab_${tabElements.tabName}_${index}`}>
          <MarginBox ml={15} mt={15}>
            <Text type={'h3'}>{item.listHeader}</Text>
          </MarginBox>
          <MarginBox mb={15} />
          {renderSection(item, sparePartsView, refLocal, type)}
          {index + 1 < items.length && <MarginBox mt={15} />}
        </React.Fragment>
      ))}
    </Tab>
  );
}

export function ProductMenu({
  refLocal,
  sparePartsView,
}: {
  refLocal: DHReferenceLocal;
  sparePartsView: SparePartsViewType;
}) {
  const dispatch = useDispatch();
  const selectedTab = useSelector(getSelectedTab);

  return (
    <STabs
      id={PRODUCT_MENU_TABS}
      type="card"
      size={'large'}
      activeKey={selectedTab}
      onChange={(value) => {
        dispatch(setSelectedTab(value as LocalTabType));
      }}
    >
      {LocalTab(refLocal, sparePartsView, 'equivalent_references')}
      {LocalTab(refLocal, sparePartsView, 'substitute_references')}
      {LocalTab(refLocal, sparePartsView, 'linked_references')}
      {LocalTab(refLocal, sparePartsView, 'kit')}
    </STabs>
  );
}
